import ExpiredTokenModal, {
  refreshSessionModalLogoutButtonId,
} from 'components/modals/ExpiredTokenModal';
import AccessDeniedModal, {
  accessDeniedModalContainer,
} from 'components/modals/AccessDeniedModal';
import { environmentVars } from 'constants/services';
import { FeatureFlag } from 'featureFlags';
import { getAppServices } from 'providers/app-services';
import { createI18nProvider } from 'providers/i18nProvider';
import React from 'react';
import ReactDOM from 'react-dom';

class GQLRequest {
  _endpoint = environmentVars.API_GATEWAY_GRAPHQL_ENDPOINT;

  _variables = {};

  _query = '';

  constructor() {
    const { featureFlagService } = getAppServices();
    const useAlternateUrl = featureFlagService.hasFeature(
      FeatureFlag.UseAlternateURLs
    );
    this._endpoint = useAlternateUrl
      ? environmentVars.API_GATEWAY_GRAPHQL_ENDPOINT_02
      : environmentVars.API_GATEWAY_GRAPHQL_ENDPOINT;
    return this;
  }

  endpoint = (endpoint: string): GQLRequest => {
    this._endpoint = endpoint;

    return this;
  };

  variables = (
    vars: Record<string, boolean | string | number | object | null>
  ): GQLRequest => {
    this._variables = vars;

    return this;
  };

  query = (query: string): GQLRequest => {
    this._query = query;

    return this;
  };

  run = async (): Promise<Response> => {
    const { authService, featureFlagService } = getAppServices();
    const { accessToken, idToken } = authService.getCredentials();

    const areCredentialsExpired = authService.areCredentialsExpired();
    const isModalOpen = Boolean(
      document.getElementById(refreshSessionModalLogoutButtonId)
    );

    if (areCredentialsExpired && !isModalOpen) {
      this.showRefreshModal();

      throw new Error(
        `GQLRequest error: unable to complete request: 
          Expired token
        `
      );
    }

    const isStudioPrivateEnabled = featureFlagService.hasFeature(
      FeatureFlag.UseStudioPrivateApi
    );

    const resp = await fetch(this._endpoint, {
      method: 'POST',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken.length > 0
          ? {
              Authorization: `Bearer ${accessToken}`,
            }
          : {}),
        ...(idToken.length > 0 && isStudioPrivateEnabled
          ? {
              'x-bluebeam-token': idToken,
            }
          : {}),
      },
      body: JSON.stringify({
        query: this._query,
        variables: this._variables,
      }),
    });

    if (!resp.ok) {
      const text = await resp.text();

      throw new Error(
        `GQLRequest error: unable to complete request: 
          endpoint: ${this._endpoint}
          query: ${this._query}
          variables: ${JSON.stringify(this._variables)}
          body: ${text}
        `
      );
    }

    return resp;
  };

  json = async <T>(): Promise<T> => {
    const resp = await this.run();

    const { data, errors } = await resp.json();

    if (errors && errors.length > 0) {
      if (errors[0].message === 'Unauthorized') {
        this.showAccessDeniedModal();
      }
      throw new Error(
        `GQLRequest error: unable to get json: 
          errors: ${JSON.stringify(errors)}
          endpoint: ${this._endpoint}
          query: ${this._query}
          variables: ${JSON.stringify(this._variables)}
        `
      );
    }

    return data;
  };

  showAccessDeniedModal = (): void => {
    const I18nProvider = createI18nProvider();

    const rootEl = document.getElementById('root');

    const isModalOpen = Boolean(
      document.getElementById(accessDeniedModalContainer)
    );

    const closeModal = (): boolean => {
      const modal = document.getElementById(accessDeniedModalContainer);
      if (modal) {
        return false;
      }
      return true;
    };

    if (rootEl && !isModalOpen) {
      const el = document.createElement('div');
      rootEl.append(el);

      ReactDOM.render(
        React.createElement(
          I18nProvider,
          {},
          React.createElement(AccessDeniedModal, { closeModal })
        ),
        el
      );
    }
  };

  showRefreshModal = (): void => {
    const I18nProvider = createI18nProvider();

    const rootEl = document.getElementById('root');

    const isModalOpen = Boolean(
      document.getElementById(refreshSessionModalLogoutButtonId)
    );

    if (rootEl && !isModalOpen) {
      const el = document.createElement('div');
      rootEl.append(el);

      ReactDOM.render(
        React.createElement(
          I18nProvider,
          {},
          React.createElement(ExpiredTokenModal)
        ),
        el
      );
    }
  };
}

export default GQLRequest;
